<template>
  <BfFlex>
    <BfInput
      v-model="filters.FreeTextSearch"
      :name="t('common.search.filterBrandOrModel')"
      :placeholder="t('common.search.filterBrandOrModel')"
      style="flex: 1"
      @keyup="updateSearch"
    />
  </BfFlex>

  <div class="saddle-selection__results">
    <BfLoader v-if="loading" />
    <BfList v-if="saddleData.length > 0">
      <BfListItem
        v-for="(saddle, i) in saddleData"
        :key="i"
        :text="`${saddle.SADDLE_BRAND} ${saddle.SADDLE_MODEL} (${saddle.SADDLE_WIDTH} ${t('common.unit.mm')})`"
        :state="
          saddle === selected ? ListItemState.Active : ListItemState.Enabled
        "
        @click="select(saddle)"
      />
    </BfList>
    <div v-else>
      <p v-if="!loading">{{ t('common.search.noResults') }}</p>
    </div>
  </div>

  <SelectionDetails v-if="selected">
    <SelectionDetailsItem
      :label="t('setup.saddle.width')"
      :value="`${selected.SADDLE_WIDTH} ${t('common.unit.mm')}`"
      icon="/icons/filters/saddle__width.svg"
    />
    <SelectionDetailsItem
      :label="t('setup.saddle.shape.index')"
      :value="shapeLabel"
      :icon="shapeIcon"
    />
    <SelectionDetailsItem
      :label="t('setup.saddle.anatomical')"
      :value="selected.ANATOMIC_DESIGN ? t('common.yes') : t('common.no')"
      :icon="anatomicalIcon"
    />
  </SelectionDetails>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/store/userStore'
import { useSaddleStore } from '@/store/saddleStore'
import {
  ListItemState,
  BfLoader,
  BfList,
  BfListItem,
  BfFlex,
  BfInput,
} from '@finetic/bikefitting-ui'
import { useDebounceFn } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import SelectionDetails from '@/components/setup/SelectionDetails.vue'
import SelectionDetailsItem from '@/components/setup/SelectionDetailsItem.vue'

const { t } = useI18n()
const userStore = useUserStore()
const saddleStore = useSaddleStore()
const { sessionId } = storeToRefs(userStore)
const { saddleData, filters } = storeToRefs(saddleStore)
const loading = ref(false)
const selected = ref()

const shapeKeyMap: Record<string, string> = {
  FLAT: 'flat',
  'SEMI ROUND': 'semiCurved',
  ROUND: 'curved',
}

const emit = defineEmits(['select'])

const select = (saddle: any) => {
  selected.value = saddle
  emit('select', saddle)
}

const search = () => {
  selected.value = undefined
  loading.value = true

  saddleStore.reset()
  saddleStore.retrieveSaddles(sessionId?.value ?? '').then(() => {
    loading.value = false
  })
}

const shapeLabel = computed(() => {
  const shape = selected.value?.SHAPE?.toUpperCase() ?? ''
  const key = shapeKeyMap[shape]
  return key ? t(`setup.saddle.shape.${key}`) : '-'
})

const shapeIcon = computed(() => {
  const shape = selected.value?.SHAPE?.toUpperCase() ?? ''

  switch (shape) {
    case 'FLAT':
      return '/icons/filters/saddle-shape__flat.svg'

    case 'SEMI ROUND':
      return '/icons/filters/saddle-shape__semi.svg'

    case 'ROUND':
      return '/icons/filters/saddle-shape__curved.svg'

    default:
      return '/icons/filters/saddle-shape__flat.svg'
  }
})

const anatomicalIcon = computed(() => {
  return selected.value?.ANATOMIC_DESIGN
    ? '/icons/filters/saddle-anatomical__yes.svg'
    : '/icons/filters/saddle-anatomical__no.svg'
})

const updateSearch = useDebounceFn(() => {
  search()
}, 300)

search()
</script>

<style lang="scss">
.saddle-selection {
  display: flex;
  height: 100%;

  &__results {
    margin-top: 1.2rem;
    border: solid 1px var(--color-dark-10);
    border-radius: 0.4rem;
    height: 32rem;
    overflow-y: auto;

    p {
      text-align: center;
      padding: 1.6rem;
      color: var(--color-dark-40);
    }
  }
}
</style>
